import { faAdd } from '@fortawesome/pro-regular-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  Stack,
  TextField,
  Tooltip
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useAtom } from 'jotai';
import { without } from 'lodash';
import { useCallback, useEffect } from 'react';
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useWatch
} from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import SearchableSelect from '@/components/SearchableSelect';
import {
  sensorsListAtom,
  useCreateRequest
} from '@/pages/Administration/AdministrationPcapRequests/AdministrationPcapRequests.store';
import type {
  RequestCreateDetailsDTO,
  RequestCreateDTO
} from '@/pages/Administration/AdministrationPcapRequests/AdministrationPcapRequests.types';
import { selectedOrganizationAtom } from '@/pages/Organizations';

import EMPTY_PCAP_REQUEST_DIALOG from './PcapRequestCreateDialog.constants';
import useStyles from './PcapRequestCreateDialog.styles';

const STENOGRAPHER_INFO_TEXT = (
  <span>
    Queries are written using the Stenographer Query Language. Some examples and
    documentation can be found{' '}
    <Link
      href="https://github.com/google/stenographer?tab=readme-ov-file#query-language"
      target="_blank"
      underline="none"
    >
      here.
    </Link>{' '}
    Note that any timestamp fields are automatically added by Scope and should
    not be included in the query.
  </span>
);

const PcapRequestCreateDialog = ({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { classes } = useStyles();
  const [selectedOrg] = useAtom(selectedOrganizationAtom);
  const [orgSensors] = useAtom(sensorsListAtom);

  const mutation = useCreateRequest();
  const form = useForm<RequestCreateDetailsDTO>({
    defaultValues: EMPTY_PCAP_REQUEST_DIALOG
  });
  const { control, formState, getValues, handleSubmit, reset, setValue } = form;
  const selectedSensors = useWatch({
    control,
    name: 'sensors',
    defaultValue: []
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDelete = (sensor: string) => {
    setValue('sensors', without(selectedSensors, sensor));
  };

  const updateSensorList = useCallback((_key: string, selected: string[]) => {
    setValue('sensors', [...selected]);
  }, []);

  const getErrorMessages = () => {
    const errors = Object.values(formState.errors).map(
      (error) => error.message
    );
    if (errors.length === 0) {
      return null;
    }

    if (mutation.isError) {
      errors.push(`Failed to create request: ${mutation.error.message}`);
    }
    return (
      <Alert key={uuidv4()} severity="error" sx={{ mb: 2 }}>
        {errors.map((error) => (
          <div key={error}>{error}.</div>
        ))}
      </Alert>
    );
  };

  const onSubmit: SubmitHandler<RequestCreateDetailsDTO> = (newRequest) => {
    const fullRequest: RequestCreateDTO = {
      details: newRequest,
      request_type: 'pcap',
      organization_id: selectedOrg?.id,
      status: 'pending'
    };
    mutation.mutate(fullRequest, {
      onSuccess: () => {
        handleClose();
      }
    });
  };

  // auto select sensor if there is only one
  useEffect(() => {
    if (orgSensors.length === 1 && selectedSensors.length === 0) {
      setValue('sensors', [`${orgSensors[0].value}`]);
    }
  }, [orgSensors]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Request</DialogTitle>
      <DialogContent>
        {selectedOrg?.id === 0 && (
          <Alert severity="warning" sx={{ margin: 2 }}>
            Please select an organization from the topbar
          </Alert>
        )}
        {orgSensors.length === 0 && (
          <Alert severity="warning" sx={{ margin: 2 }}>
            No sensors available to create a request
          </Alert>
        )}
        {orgSensors.length > 0 && selectedOrg?.id !== 0 && (
          <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit)} noValidate={false}>
              <Stack direction="column" spacing={2} sx={{ padding: 2 }}>
                {getErrorMessages()}
                <Stack direction="row" spacing={2}>
                  <Stack>
                    <InputLabel htmlFor="start-input" required>
                      Start Date
                    </InputLabel>
                    <Controller
                      name="start"
                      control={control}
                      render={({ field }) => (
                        <FormControl id="start-input" fullWidth>
                          <DateTimePicker
                            disableFuture
                            slotProps={{
                              textField: {
                                error: !!formState.errors.end
                              }
                            }}
                            {...field}
                          />
                        </FormControl>
                      )}
                      rules={{
                        required: 'Start Date is required',
                        validate: (value) =>
                          value < getValues('end') ||
                          'Start date must be before end date'
                      }}
                    />
                  </Stack>
                  <Stack>
                    <InputLabel htmlFor="end-input" required>
                      End Date
                    </InputLabel>
                    <Controller
                      name="end"
                      control={control}
                      render={({ field }) => (
                        <FormControl id="end-input" fullWidth>
                          <DateTimePicker
                            disableFuture
                            slotProps={{
                              textField: {
                                error: !!formState.errors.end
                              }
                            }}
                            {...field}
                          />
                        </FormControl>
                      )}
                      rules={{
                        required: 'End Date is required',
                        validate: (value) =>
                          value > getValues('start') ||
                          'End date must be after start date'
                      }}
                    />
                  </Stack>
                </Stack>
                <Stack>
                  <InputLabel htmlFor="purpose-input">Purpose</InputLabel>
                  <Controller
                    name="purpose"
                    control={control}
                    render={({ field }) => (
                      <TextField id="purpose-input" {...field} fullWidth />
                    )}
                  />
                </Stack>
                <Stack>
                  <InputLabel htmlFor="query-input">
                    Query *
                    <Tooltip
                      title={STENOGRAPHER_INFO_TEXT}
                      classes={{ tooltip: classes.queryInfo }}
                    >
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className={classes.infoIcon}
                      />
                    </Tooltip>
                  </InputLabel>
                  <Controller
                    name="query"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        id="query-input"
                        {...field}
                        fullWidth
                        required
                        error={!!formState.errors.query}
                      />
                    )}
                    rules={{ required: 'Query is required' }}
                  />
                </Stack>
                <Stack>
                  <InputLabel htmlFor="query-input">
                    Selected Sensors
                  </InputLabel>
                  {selectedSensors?.length > 0 && (
                    <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 1 }}>
                      {selectedSensors.map((sensor) => (
                        <Chip
                          key={sensor}
                          label={sensor}
                          variant="filled"
                          onDelete={() => handleDelete(sensor)}
                        />
                      ))}
                    </Stack>
                  )}
                  <Controller
                    name="sensors"
                    control={control}
                    render={({ field }) => (
                      <SearchableSelect
                        searchable
                        selectedOptions={field.value}
                        showCounts={false}
                        field="sensors"
                        filterOptions={orgSensors}
                        onApply={updateSensorList}
                        titleButton="button"
                        titleButtonProps={{
                          label: 'Add Sensors',
                          startIcon: <FontAwesomeIcon icon={faAdd} size="1x" />,
                          variant: 'outlined',
                          disabled: orgSensors.length === 0,
                          color: formState.errors.sensors ? 'error' : 'primary'
                        }}
                      />
                    )}
                    rules={{
                      validate: (value) =>
                        value.length > 0 || 'At least one sensor is required'
                    }}
                  />
                </Stack>
              </Stack>
            </form>
          </FormProvider>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button
          disabled={selectedOrg?.id === 0 || orgSensors.length === 0}
          onClick={handleSubmit(onSubmit)}
          color="primary"
          variant="contained"
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PcapRequestCreateDialog;
